import { useState } from 'react';
import { Badge, Button, Input, useToast } from '../ui';
import { supabase } from '../../lib/supabase';
import { useUser } from '../../context/UserContext';
import { AdsStats, formatRate } from './meta';

interface AdsCloseRateProps {
  stats: AdsStats | null;
  /** Re-read prospects_ads_stats after the override changes. */
  onChanged: () => void;
}

/**
 * The close rate Project Signal multiplies each Qualified Lead's deal value by, with a manual
 * override. The override lives in ads_signal_settings and prospects_ads_stats.close_rate already
 * resolves to it, so the uploader follows it on its next run. Sent cards keep the rate they went at.
 */
export default function AdsCloseRate({ stats, onChanged }: AdsCloseRateProps) {
  const { user } = useUser();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const running = stats?.running_close_rate ?? stats?.close_rate ?? null;
  const override = stats?.close_rate_override ?? null;
  const effective = stats?.close_rate ?? null;

  const saveOverride = async (rate: number | null) => {
    setSaving(true);
    const { data, error } = await supabase
      .from('ads_signal_settings')
      .update({ close_rate_override: rate, updated_at: new Date().toISOString(), updated_by: user?.id ?? null })
      .eq('id', true)
      .select('id');
    setSaving(false);
    // RLS lets only admins update; a blocked update returns no rows rather than an error.
    if (error || !data?.length) {
      toast.error('Close rate not saved', error?.message ?? 'Only admins can change the override.');
      return;
    }
    toast.success(rate == null ? 'Override cleared' : `Close rate pinned at ${formatRate(rate)}`, 'Applies from the next Google Ads upload');
    setEditing(false);
    onChanged();
  };

  const draftPct = Number(draft);
  const draftValid = draft.trim() !== '' && Number.isFinite(draftPct) && draftPct > 0 && draftPct <= 100;

  const startEditing = () => {
    const seed = override ?? running;
    setDraft(seed != null ? String(Math.round(Number(seed) * 1000) / 10) : '');
    setEditing(true);
  };

  return (
    <div
      className="ck-statcard"
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 'var(--space-4) var(--space-6)',
        marginBottom: 'var(--space-4)',
        padding: 'var(--space-4) var(--space-5)',
        background: 'var(--surface-card)',
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-rest)',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}>
        <span className="t-eyebrow">Google Ads · Qualified Lead close rate</span>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <span
            className="t-num"
            style={{
              fontSize: 'var(--text-2xl)',
              fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
              letterSpacing: 'var(--tracking-tight)',
              lineHeight: 'var(--leading-tight)',
              color: 'var(--text-body)',
            }}
          >
            {formatRate(effective)}
          </span>
          {override != null ? <Badge tone="warn">Override</Badge> : <Badge tone="neutral">Running avg</Badge>}
        </div>
      </div>

      <div style={{ flex: '1 1 280px', minWidth: 0, fontSize: 'var(--text-sm)', color: 'var(--text-faint)', lineHeight: 1.5 }}>
        {stats ? (
          <>
            <div>
              Running avg{' '}
              <span className="t-num" style={{ color: 'var(--text-body)' }}>
                {formatRate(running)}
              </span>{' '}
              · {stats.won ?? 0} won / {stats.lost ?? 0} lost, qualified cards, last 365 days
            </div>
            {/* Explainer only; dropped on phones so the board isn't pushed a full screen down */}
            <div className="hidden md:block">Qualified Lead value = deal × this rate. Sent cards keep theirs.</div>
          </>
        ) : (
          <div>Close rate unavailable.</div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexShrink: 0 }}>
        {editing ? (
          <>
            <div style={{ width: '104px' }}>
              <Input
                fieldSize="sm"
                type="number"
                min="1"
                max="100"
                step="0.1"
                icon="percent"
                aria-label="Close rate override, percent"
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && draftValid) saveOverride(draftPct / 100);
                  if (e.key === 'Escape') setEditing(false);
                }}
              />
            </div>
            <Button size="sm" icon="check" disabled={!draftValid || saving} onClick={() => saveOverride(draftPct / 100)}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button size="sm" variant="ghost" disabled={saving} onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            {override != null ? (
              <Button size="sm" variant="ghost" icon="rotate-ccw" disabled={saving} onClick={() => saveOverride(null)}>
                Use running avg
              </Button>
            ) : null}
            <Button size="sm" variant="secondary" icon="pencil" disabled={!stats} onClick={startEditing}>
              {override != null ? 'Edit override' : 'Override'}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
